<template>
  <div
    v-intersect
    class="filter-city"
    @intersect:enter="onShow"
  >
    <CommonSelectCity
      v-model="selectedCity"
      :placeholder-mask="$t('catalog.filters.all_cities')"
      enabled-reset
    />

    <ul class="cities-default">
      <CityDefaultItem
        v-if="hasSelected"
        :to="filterWithLinks ? getRouteNoCity() : ''"
        :name="$t('catalog.filters.country')"
        @click.prevent="selectedCity = null"
      />

      <CityDefaultItem
        v-for="city in getDefault"
        :key="`default_${city.uuid}`"
        :to="filterWithLinks ? getRouteToCity(city) : ''"
        :name="city.short_name"
        @click.prevent="selectedCity = city"
      />
    </ul>
  </div>
</template>

<script setup lang="ts">
import { CITY_FILTER_KEY } from '@/constants/common'
import type { CatalogFilter, FilterItemChangePayload } from '~/types/catalog'
import type { SearchCity, SearchCityDefault } from '~/types/common'

type Props = {
  filter: CatalogFilter
  selected: string
}
type Emits = {
  (e: 'on-change' | 'on-show', value: FilterItemChangePayload): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const localePath = useLocalePath()
const route = useRoute()
const catalogFiltersStore = useCatalogFiltersStore()

const filterWithLinks = false

const {
  citiesDefault,
  livingCity,
  setCityCookies,
  getCityShortName,
  latestSelectedCity
} = useLivingCity(CITY_FILTER_KEY)

const { filterCity } = storeToRefs(catalogFiltersStore)

const hasSelected = computed(() => props.selected.length > 0)

const userCity = computed<SearchCity | null>(
  () => latestSelectedCity.value || livingCity.value || null
)

const getDefault = computed<SearchCityDefault[]>(() => {
  const filteredCities = citiesDefault.value.filter(
    (city) => city.uuid !== props.selected && city.uuid !== userCity.value?.uuid
  )

  return userCity.value
    ? [{ ...userCity.value, short_name: getCityShortName(userCity.value.name) }, ...filteredCities]
    : filteredCities
})

const selectedCity = computed({
  get: () => filterCity.value,
  set: (city: SearchCity | null) => {
    if (props.selected !== city?.uuid || '') {
      onChange(city)
    }
    nextTick(() => {
      catalogFiltersStore.changeFilterCity(city || null)
    })
  }
})

const getRouteNoCity = () => {
  const regular = new RegExp(`&?${props.filter.code}=${props.selected}&?`)

  return unref(route).path.replace(regular, '')
}

const getRouteToCity = (city: SearchCity) => {
  const categoryFilters: string = `${props.filter.code}=${city.uuid}`
  const {
    path,
    params: routeParams
  } = unref(route)

  return routeParams.categoryFilters
    ? localePath({ params: { ...routeParams, categoryFilters } })
    : `${path}/${categoryFilters}`
}

const onChange = (city: SearchCity | null) => {
  emit('on-change', {
    filter_code: props.filter.code,
    filter_name: props.filter.name,
    value: city?.uuid,
    label: city?.name || '',
    isOne: true
  })
  setCityCookies(city)
}

const onShow = () => {
  emit('on-show', {
    filter_code: props.filter.code,
    filter_name: props.filter.name
  })
}

</script>

<style lang="scss" scoped>
.cities-default {
  padding: 0;
  margin: 1.2rem 0 0;
  list-style-type: none;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &__it {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    padding: 0;
    display: inline;
    margin-right: 0.8rem;
    width: 100%;
  }
}
</style>
